// src/pages/CreditNotev2.jsx
import { colors as themeColors } from '../utils/theme';
import React, { useEffect, useState, useCallback } from "react";
import { Box, Button, Typography, IconButton, Dialog, DialogTitle, DialogContent, Snackbar, Alert, useTheme, useMediaQuery, Paper, Stack, Divider } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import axios from "axios";
import { saveAs } from "file-saver";

const API_BASE = "http://192.168.1.248:3103";

// CSV export helper
const jsonToCsv = (rows) => {
    if (!rows.length) return "";
    const headers = Object.keys(rows[0]);
    const escape = (s) => `"${(s ?? "").toString().replace(/"/g, '""')}"`;
    const lines = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))];
    return lines.join("\n");
};

function CreditNotev2() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/api/get-credit-note`);
            setData(res.data || []);
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: "Failed to fetch credit notes.", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleExport = () => {
        const csv = jsonToCsv(data);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "credit-notes.csv");
    };
    const handleView = (id) => { window.open(`${window.location.origin}/creditNote/${id}`, "_blank"); };

    const columns = [
        {
            field: "creditNoteNumber", headerName: "Credit Note", flex: 0.5,
            renderCell: ({ value }) => (
                <Typography fontWeight={600} color="primary.main">{value}</Typography>
            )
        },
        {
            field: "companyName", headerName: "Client", flex: 1.35,
            renderCell: ({ row }) => (
                <Box display="flex" flexDirection="column" justifyContent="center" minHeight={50}>
                    <Typography variant="body2" fontWeight={600}>{row.companyName}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.referenceNumber || "No Reference"}</Typography>
                </Box>
            )
        },
        {
            field: "location", headerName: "Location", flex: 0.8,
            renderCell: ({ row }) => (
                <Box display="flex" flexDirection="column" justifyContent="center" minHeight={50}>
                    <Typography variant="body2">{row.city || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.state || "—"}</Typography>
                </Box>
            )
        },
        {
            field: "installBy", headerName: "Installed By", flex: 0.65,
            renderCell: ({ value }) => value || "—"
        },
        {
            field: "actions", headerName: "Actions", flex: 0.24, sortable: false, headerAlign: 'right', align: 'right',
            renderCell: (params) => (
                <Stack direction="row" spacing={1} justifyContent="flex-end" width="100%">
                    <IconButton size="small" color="primary" onClick={() => handleView(params.row.creditNoteId)}>
                        <VisibilityOutlinedIcon />
                    </IconButton>
                </Stack>
            )
        }
    ];

    return (
        <Box sx={{ p: 1, axHeight: "100vh" }}>
            {/* Page Header */}
            <Box sx={{ mb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>📄 Credit Notes</Typography>
                        <Typography variant="body2" color="text.secondary">Manage and track all issued credit notes.</Typography>
                    </Box>
                    <Button startIcon={<DownloadIcon />} variant="contained" onClick={handleExport} sx={{ borderRadius: 2 }} > Export CSV </Button>
                </Stack>
            </Box>

            {/* Table Display */}
            <Paper elevation={0} sx={{ mt: 1, bgcolor: themeColors.neutral.surface, borderRadius: 2 }}>
                <Box sx={{ maxHeight: '82vh', maxWidth: '100%', overflow: 'auto' }}>
                    <DataGrid rows={data} columns={columns} getRowId={(row) => row.creditNoteId} loading={loading} autoHeight disableRowSelectionOnClick density="compact" getRowHeight={() => 'auto'}
                        sx={{
                            /* header background */
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: themeColors.primary.dark,
                                color: themeColors.neutral.surface,
                                fontWeight: 'bold',
                            },

                            /* header text (cell title) */
                            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainer': {
                                color: themeColors.neutral.surface, // ensures header text is white
                            },

                            /* all SVG icons inside header cells (generic) */
                            '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                                color: `${themeColors.neutral.surface} !important`,
                                opacity: 1,
                            },

                            /* header icon buttons (container for three-dots menu button, etc.) */
                            '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
                                color: `${themeColors.neutral.surface} !important`,
                                opacity: 1,
                            },

                            /* specific DataGrid icon classes (menu/filter/sort) */
                            '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
                                color: `${themeColors.neutral.surface} !important`,
                                opacity: 1,
                            },

                            /* Force the SVG path/fill to pure white for sort icon specifically */
                            '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon svg, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon svg path': {
                                fill: `${themeColors.neutral.surface} !important`,
                                stroke: `${themeColors.neutral.surface} !important`,
                                color: `${themeColors.neutral.surface} !important`,
                                opacity: 1,
                            },

                            /* Ensure other icons (menu/filter) remain white */
                            '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon svg path, & .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon svg path': {
                                fill: `${themeColors.neutral.surface} !important`,
                                stroke: `${themeColors.neutral.surface} !important`,
                                opacity: 1,
                            },

                            /* keep row striping */
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.primary.tint },

                            /* remove header focus outline */
                            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                outline: 'none',
                            },
                        }}
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } }, }}
                    />
                </Box>
            </Paper>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} >
                <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default CreditNotev2;